<script lang="ts">
	// Design system components
	import {
		Toggle,
		Modal,
		Container,
		Button,
		Text,
		Checkbox,
		Alert,
	} from '@Shelly/shelly-design-system';
	import { toast } from '@Shelly/shelly-design-system';
	// Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../../../services/http';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	// Stores
	import {
		getDeviceStatus,
		readDeviceStatus,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';

	import type { Device } from 'src/types/Device';
	import { get } from 'svelte/store';
	// Helpers
	import { capFirstChar } from '../../../../../helpers/utilities';
	import {
		extractWallDisplayComponent,
		deleteWallDisplayExtention,
		isWallDisplaySeparateComponentsSupported,
		disableWallDisplayExtention,
		showSuccessToast,
	} from 'src/helpers/wall_display_extract/extracting_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	$: statusStore = getDeviceStatus(device.id);
	export let device;

	let showModalPrompt = false;

	$: extractionValueToggle = typeof get(devices)[`${real_id(device.id)}_100`] !== 'undefined';
	let extractionValueToggleInModal = false;

	$: enabled = !!device?.media_player_enabled;

	const isExtractionSupported = isWallDisplaySeparateComponentsSupported(
		readDeviceSettings(device.id),
	);

	const initMediaPlayer = async (nextEnabledState) => {
		let editDevice = {
			...device,
			media_player_enabled: nextEnabledState,
		};

		let res = await postReq('interface/device/save', {
			id: editDevice.id,
			data: JSON.stringify(editDevice),
		});

		if (res.isok) {
			devices.update(editDevice);
		} else {
			enabled = device.media_player_enabled;
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
		}
	};

	const disableDeleteExtractedMediaPlayer = async (nextEnabledState) => {
		await disableWallDisplayExtention({
			id: real_id(device.id) + '_100',
		} as Device);
		await deleteWallDisplayExtention({
			id: real_id(device.id) + '_100',
		} as Device);

		let editDevice = {
			...device,
			media_player_enabled: nextEnabledState,
		};
		devices.update(editDevice);
	};

	const enableMediaPlayer = async () => {
		const nextEnabledState = !enabled;

		if (nextEnabledState) {
			await initMediaPlayer(nextEnabledState);
		} else {
			await disableDeleteExtractedMediaPlayer(nextEnabledState);
		}

		setSidePanelState({ showSettingsModal: false });
	};

	const enableExtractMediaPlayer = async () => {
		const nextExtractionState = !extractionValueToggle;

		if (nextExtractionState) {
			await extractWallDisplayComponent(device, 'media_player');
		} else {
			let isPlaying = $statusStore?.media?.playback?.enable;
			if (isPlaying) {
				//stopping media player if media is playing on device
				await gen2Request(device, 'Media.MediaPlayer.PlayOrPause', {
					config: {},
				});
			}

			await deleteWallDisplayExtention({
				id: real_id(device.id) + '_100',
			} as Device);
		}
	};

	const handleModalPrompt = async () => {
		// open modal only if enabled state is false
		const previousEnabledState = enabled;

		if (previousEnabledState === false) {
			showModalPrompt = true;
		} else {
			await enableMediaPlayer();
			routeToDeviceSettings.setTarget(device.id, 'main_controls');
			showSuccessToast();
		}
	};
</script>

{#if !isExtractionSupported}
	<Checkbox
		size="l"
		class="mb-s"
		bind:checked={enabled}
		on:click={async () => {
			await enableMediaPlayer();
			showSuccessToast();

			setSidePanelState({ showSettingsModal: false });
		}}
		id="checkbox-mre7kel"
	>
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>
{/if}

{#if isExtractionSupported}
	<Checkbox
		size="l"
		class="mb-s"
		bind:checked={enabled}
		on:click={() => (showModalPrompt = true)}
		id="checkbox-i6aeuls"
	>
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	<Toggle
		checked={extractionValueToggle}
		size="m"
		disabled={!enabled}
		on:click={async () => {
			await enableExtractMediaPlayer();
			showSuccessToast();
		}}
		id="toggle-azyyhld"
	>
		{$format('extract_media_player_extension', {
			default: 'Extract Media Player extension',
		})}
	</Toggle>
	<Alert
		type="info"
		heading={$format('alert_heading_extracted_media_player_wall_display', {
			default:
				'The extracted media player will operate as an independent device, functioning separately from the wall display.',
		})}
	/>

	<Modal
		bind:showModal={showModalPrompt}
		on:close={() => (showModalPrompt = false)}
		title={!enabled
			? capFirstChar(
					$format('modal_title_enable_media_player', {
						default: 'Enable media player',
					}),
				)
			: $format('modal_title_disable_media_player', {
					default: 'Disable media player',
				})}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<Text class="mb-m"
					>{$format('text_media_player_enable_disable', {
						default: `Are you sure you want to ${enabled ? 'disable' : 'enable'}  this setting?`,
						values: {
							prop: enabled
								? $format('button_disable').toLowerCase()
								: $format('button_enable').toLowerCase(),
						},
					})}</Text
				>
				{#if !enabled}
					<Toggle
						checked={extractionValueToggleInModal}
						size="m"
						on:click={() => {
							extractionValueToggleInModal = !extractionValueToggleInModal;
						}}
						id="toggle-cmz766l"
					>
						{$format('toggle_extract_media_player_as_well', {
							default: 'Would you like to extract the media player as well?',
						})}
					</Toggle>
				{/if}
			</Container>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="width-md-m width-sm-full mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={async () => {
					showModalPrompt = false;
				}}>{capFirstChar($format('button_cancel', { default: 'Cancel' }))}</Button
			>
			<Button
				class="width-md-m width-sm-full"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={async () => {
					showModalPrompt = false;

					if (extractionValueToggleInModal) {
						await Promise.all([enableMediaPlayer(), enableExtractMediaPlayer()]);
					} else {
						await enableMediaPlayer();
					}
					setSidePanelState({ type: 'device', id: device.id, showSettingsModal: false });
					showSuccessToast();
				}}
				>{!enabled
					? capFirstChar($format('button_enable', { default: 'Enable' }))
					: capFirstChar($format('button_disable', { default: 'Disable' }))}</Button
			>
		</div>
	</Modal>
{/if}
